/**
 * AgreeRateDialog — the agree-rate path (ruling 54): the composer's Confirm
 * rate pill, ThreadHeader's "Agree rate…" item and the profile drawer. It
 * records the agreed rate (rate_agreed → With brand). Reuses CounterRatesDialog
 * as the rate grid.
 *
 * Prefill: re-agreeing (a revise after hand-off too) seeds from the agreed deliverables; a fresh agree from
 * the latest creator quote, or empty when there is none (ruling 76).
 *
 * An optional acknowledgement (off by default) goes out as an ordinary thread
 * reply only after the agree succeeds, and then clears the thread's composer
 * and its saved draft (o-e2e2 N6). If that send fails the rate stays agreed and
 * the text lands in the composer instead.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { OutreachQuote, OutreachRate, OutreachRow } from '@discovr/types';
import { fmtDeliverableType, isMain, isWithBrandStatus, resolvePlatform } from '@discovr/types';
import { useOutreachNegotiation } from '@discovr/features';
import { Checkbox } from '../../../components/ui/checkbox';
import { CounterRatesDialog } from './CounterRatesDialog';
import { fmtRate } from './QuoteBlock';
import { fmtAgreedMoney } from './agreedMoney';
import { roundSuffix } from './roundLabel';
import { errorCopy, codeOf, detailsOf } from './sendLabels';
import { hasThread } from './handoffStates';
import type { ComposerDraft } from './ThreadComposer';
import { toastSendStatus } from './sendStatusToast';
import { ComposerEditor, type ComposerEditorHandle } from './composerEditor/ComposerEditor';
import { hasFormatting } from './composerEditor/stripHtmlAttributes';
import { composerHtmlFromText } from './composerEditor/headless';

export interface AgreeRateDialogProps {
  open: boolean;
  campaignId: string;
  row: OutreachRow;
  latestQuote?: OutreachQuote;
  onClose: () => void;
  /** Receives an acknowledgement that could not be sent, so it waits in the thread's composer. */
  onAcknowledge?: (entryId: string, draft: ComposerDraft) => void;
  /** The acknowledgement went out: the thread's composer drops what it held (o-e2e2 N6). */
  onAcknowledged?: (entryId: string) => void;
}

type AckDraft = { body: string; bodyHtml: string };

const joinList = (items: string[]): string => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/** "a Reel", "an Auto DM" (owner 2026-10-12: "for a Reel"); a plural label ("Stories") or an adjective one
 *  ("Integrated") takes no article. */
export function deliverableWithArticle(type: string): string {
  const label = fmtDeliverableType(type);
  if (/(s|ed)$/i.test(label)) return label;
  return `${/^[aeiou]/i.test(label) ? 'an' : 'a'} ${label}`;
}

/** Owner rulings: it names the amounts (2026-10-09), never says the rate is agreed, and makes no promise or claim about
 *  what happens next (2026-10-20). A converted rate restates the creator's own quote, not our ₹ figure (E9-97). */
export function acknowledgementText(rates: OutreachRate[]): string {
  const listed = joinList(
    rates.map((r) => `${r.originalAmount && r.originalCurrency ? fmtRate(r.originalAmount, r.originalCurrency) : fmtRate(r.amount, r.currency)} for ${deliverableWithArticle(r.deliverable)}`),
  );
  if (rates.length === 1) return `Hi {first_name},\n\nThanks for sharing your rate: ${listed}.`;
  return `Hi {first_name},\n\n${rates.length > 1 ? `Thanks for sharing your rates: ${listed}.` : 'Thanks for sharing your rates.'}`;
}

const fromAgreed = (row: OutreachRow): OutreachRate[] =>
  (row.agreedDeliverables ?? []).map((d) => ({
    deliverable: d.type,
    amount: d.basePrice,
    currency: d.currency ?? 'INR',
    ...(d.originalAmount && d.originalCurrency ? { originalAmount: d.originalAmount, originalCurrency: d.originalCurrency } : {}),
  }));
/** The same deliverables at the same figures as the row already has agreed: Go writes nothing for it (E9-114). */
export function sameAsAgreed(row: OutreachRow, rates: OutreachRate[]): boolean {
  const agreed = row.agreedDeliverables ?? [];
  return agreed.length > 0 && agreed.length === rates.length && rates.every((r) => agreed.some((d) => d.type === r.deliverable && d.basePrice === r.amount));
}
const fromQuote = (q?: OutreachQuote): OutreachRate[] => (q?.rates ?? []).map(({ deliverable, amount, currency }) => ({ deliverable, amount, currency }));

export function AgreeRateDialog({ open, campaignId, row, latestQuote, onClose, onAcknowledge, onAcknowledged }: AgreeRateDialogProps) {
  const helpId = useId();
  const neg = useOutreachNegotiation(campaignId, row.entryId);
  const [error, setError] = useState<string | null>(null);
  const name = row.displayName ?? row.username;
  const withBrand = isWithBrandStatus(row.status);
  // Read once: the live row turns rate_provided after the agree, but a sourced creator still has no thread to reply in.
  const [hadThread] = useState(() => hasThread(row));
  const offerAck = !withBrand && hadThread && !!onAcknowledge;
  const [ackOn, setAckOn] = useState(false);
  const initial = row.agreedDeliverables?.length ? fromAgreed(row) : fromQuote(latestQuote);
  const [rates, setRates] = useState<OutreachRate[]>(initial);
  const generated = acknowledgementText(rates);
  // null until the admin types or formats: the text follows the rate grid until then.
  const [typed, setTyped] = useState<AckDraft | null>(null);
  const ack: AckDraft = typed ?? { body: generated, bodyHtml: '' };
  const editorHandle = useRef<ComposerEditorHandle>(null);
  const following = typed === null;
  useEffect(() => {
    if (ackOn && following) editorHandle.current?.replaceWithPlainText(generated);
  }, [ackOn, following, generated]);
  const onAckChange = (body: string, bodyHtml: string) => {
    if (following && body === generated && !hasFormatting(bodyHtml)) return;
    setTyped({ body, bodyHtml });
  };
  const onRatesChange = useCallback((next: OutreachRate[]) => setRates(next), []);
  const summary = (rates: OutreachRate[]) =>
    rates
      .map((r) => `${fmtAgreedMoney(r.amount, r.originalAmount && r.originalCurrency ? { amount: r.originalAmount, currency: r.originalCurrency } : undefined, r.currency)} ${fmtDeliverableType(r.deliverable)}`)
      .join(' · ');

  // A reply keeps the thread's subject, so only the body is sent.
  const sendAcknowledgement = (draft: AckDraft) => {
    const bodyHtml = draft.bodyHtml || composerHtmlFromText(draft.body);
    neg.send.mutate(
      { kind: 'reply', body: draft.body, bodyHtml, clearComposerDraft: true },
      {
        onSuccess: (result) => {
          toastSendStatus('Acknowledgement', result);
          onAcknowledged?.(row.entryId);
          onClose();
        },
        onError: (e) => {
          toast.error(`The rate is agreed, but the acknowledgement was not sent: ${errorCopy(codeOf(e), detailsOf(e))} It is waiting in the reply box.`);
          onAcknowledge?.(row.entryId, { kind: 'reply', body: draft.body, bodyHtml });
          onClose();
        },
      },
    );
  };

  const ackText = ack.body.trim() ? ack : null;
  const extra = offerAck ? (
    <div className="space-y-2 border-t border-border pt-3">
      <label className="flex items-center gap-2 text-sm text-foreground">
        <Checkbox checked={ackOn} onCheckedChange={(v) => setAckOn(v === true)} aria-label="Send acknowledgement" aria-describedby={helpId} />
        Send acknowledgement
      </label>
      <p id={helpId} className="text-xs text-muted-foreground">
        Sent as a reply in this thread once the rate is agreed.
      </p>
      {ackOn && !following ? (
        <button type="button" className="text-xs font-medium text-(--brand-600) hover:underline" onClick={() => setTyped(null)}>
          Reset to generated text
        </button>
      ) : null}
      {ackOn && (
        <div className="overflow-hidden rounded-md border border-input">
          <ComposerEditor
            handleRef={editorHandle}
            initialText={ack.body}
            initialHtml={typed?.bodyHtml}
            ariaLabel="Acknowledgement email"
            allowLink={false}
            autoFocus={false}
            liveRegion={false}
            placeholder="Write the acknowledgement…"
            onChange={onAckChange}
            onSubmitShortcut={() => undefined}
            renderFooter={(toolbar) => <div className="border-t border-border bg-[#fafafa] px-3 py-2">{toolbar}</div>}
          />
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <CounterRatesDialog
      open={open}
      title="Agree a rate"
      description={
        latestQuote?.bundle
          ? `The creator quoted ${fmtRate(latestQuote.bundle.amount, latestQuote.bundle.currency)} as a package for the deliverables below. ${
              initial.filter((r) => isMain(r.deliverable, resolvePlatform(row.platform) ?? 'instagram')).length > 1
                ? 'Enter it against the main deliverable it is for.'
                : 'Enter it on the main deliverable and leave the add-ons at 0.'
            }`
          : withBrand
          ? 'The brand will be asked to approve the revised rate again.'
          : `This is the rate the brand will see when you move ${name} to the brand dashboard.`
      }
      submitLabel={offerAck && ackOn && ackText ? 'Agree rate and send' : 'Agree rate'}
      platform={row.platform ?? 'instagram'}
      quote={latestQuote}
      initial={initial}
      convertToInr
      singleMain
      busy={neg.agreeRate.isPending || neg.send.isPending}
      error={error ?? undefined}
      extra={extra}
      onRatesChange={onRatesChange}
      onSubmit={(rates) => {
        setError(null);
        const toSend = offerAck && ackOn ? ackText : null;
        const repeat = sameAsAgreed(row, rates);
        neg.agreeRate.mutate(rates, {
          onSuccess: (result) => {
            if (repeat) toast.info(`Already agreed at ${summary(rates)}`);
            else toast.success(`Rate agreed — ${summary(rates)}${roundSuffix(result)}`);
            if (toSend) sendAcknowledgement(toSend);
            else onClose();
          },
          onError: (e) => setError(errorCopy(codeOf(e), detailsOf(e))),
        });
      }}
      onCancel={onClose}
    />
  );
}
